import React, { useState, useEffect } from "react";
import axios from "axios";
import proxy from "../../proxy";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Select,
} from "@chakra-ui/react";

const WalletModal = ({ isOpen, onClose, wallet, onSave }) => {
  const [formData, setFormData] = useState({
    address: "",
    symbol: "",
    logo: "",
    coin: "",
    amount: "",
    explorer: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (wallet) {
      setFormData({
        address: wallet.address || "",
        symbol: wallet.symbol || "",
        logo: wallet.logo || "",
        coin: wallet.coin || "",
        amount: wallet.amount || "",
        explorer: wallet.explorer || "",
      });
    } else {
      setFormData({ 
        address: "", 
        symbol: "", 
        logo: "", 
        coin: "", 
        amount: "", 
        explorer: "" 
      });
    }
  }, [wallet, isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.address || !formData.symbol) {
      toast({
        title: "Missing fields",
        description: "Address and Symbol are required",
        status: "warning",
        duration: 3000,
        position: "top",
      });
      return;
    }

    setIsLoading(true);

    try {
      if (wallet) {
        // Update
        await axios.post(`${proxy}server_mts/admin/UpdateWallet.php`, {
          id: wallet.id,
          ...formData,
        });
        toast({
          title: "Wallet updated successfully",
          status: "success",
          duration: 2000,
          position: "top",
        });
      } else {
        // Add
        await axios.post(`${proxy}server_mts/admin/AddWallet.php`, formData);
        toast({
          title: "Wallet added successfully",
          status: "success",
          duration: 2000,
          position: "top",
        });
      }

      onSave();
      onClose();
    } catch (error) {
      toast({
        title: "Error saving wallet",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent bg="gray.800" borderColor="whiteAlpha.200" border="1px solid">
        <ModalHeader color="white">
          {wallet ? "Edit Wallet" : "Add New Wallet"}
        </ModalHeader>
        <ModalCloseButton color="gray.400" />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel color="gray.300" fontSize="sm">
                Wallet Address
              </FormLabel>
              <Input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter wallet address"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                fontFamily="monospace"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="gray.300" fontSize="sm">
                Symbol
              </FormLabel>
              <Input
                name="symbol"
                value={formData.symbol}
                onChange={handleChange}
                placeholder="e.g., LTC, BTC, ETH"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                Coin Name
              </FormLabel>
              <Input
                name="coin"
                value={formData.coin}
                onChange={handleChange}
                placeholder="e.g., Litecoin, Bitcoin"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                Logo
              </FormLabel>
              <Select
                name="logo"
                value={formData.logo}
                onChange={handleChange}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              >
                <option value="" style={{ background: "#2D3748" }}>Select logo</option>
                <option value="ltclogo" style={{ background: "#2D3748" }}>LTC Logo</option>
                <option value="bchlogo" style={{ background: "#2D3748" }}>BCH Logo</option>
                <option value="dogelogo" style={{ background: "#2D3748" }}>DOGE Logo</option>
                <option value="dashlogo" style={{ background: "#2D3748" }}>DASH Logo</option>
                <option value="pollogo" style={{ background: "#2D3748" }}>POL Logo</option>
                <option value="zeclogo" style={{ background: "#2D3748" }}>ZEC Logo</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                Amount
              </FormLabel>
              <Input
                name="amount"
                type="number"
                step="0.00000001"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00000000"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              />
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                Explorer
              </FormLabel>
              <Input
                name="explorer"
                value={formData.explorer}
                onChange={handleChange}
                placeholder="e.g., litecoin, bitcoin"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={isLoading}
          >
            {wallet ? "Update" : "Add"} Wallet
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WalletModal;